import * as React from "react";

import Form from "@ant-design/compatible/es/form";
import {
    InputWithTypeahead,
    InputWithTypeaheadProps,
    TypeaheadOptionGroup,
    TypeaheadOptionOrGroup,
} from "@impero-com/common/components/typeahead";
import * as Cmp from "@impero-com/common/utils/comparison";
import { t } from "@impero-com/common/utils/i18n";
import { Button, Divider } from "@impero-com/ui";

import { MitigatingControl, ControlOverview } from "api/generated/model";
import { Drawer } from "components/drawer";
import { BaseFormDisplay } from "components/form/legacyFields/formDisplay";
import { CreateSliderInput } from "components/form/legacyFields/sliderInput";
import { TextAreaInput } from "components/form/legacyFields/textAreaInput";
import { buildControlProgramViewUrl } from "org/control/controlProgram/urlUtils";
import * as T from "org/risks/content/types";
import {
    FormCreate,
    FormRootComponentProps,
    hasError,
    mapForm,
    ObjectError,
    simpleMappers,
} from "utils/form";
import { Guid } from "utils/guid";

import { WebRoute } from "../../../../routes";

import * as theme from "./theme.css";

class AssignControlForm extends React.PureComponent<
    AssignControlProps & FormRootComponentProps<AssignControlValues>,
    AssignControlState
> {
    render = () => {
        const {
            onCancel,
            controlIdToControlAndActions,
            unassignedControls,
            form,
        } = this.props;
        const { getFieldDecorator, getFieldsError } = form;

        const onAutocomplete = (
            value: string,
            callback: (
                items: Array<TypeaheadOptionOrGroup<ControlOverview>>
            ) => void
        ): Promise<void> => {
            const lcValue = value.toLowerCase();
            const controls = unassignedControls.filter(
                (control) =>
                    `${controlIdToControlAndActions[
                        control.id
                    ].controlProgram.name.toLowerCase()} ${control.title.toLowerCase()}`.indexOf(
                        lcValue
                    ) > -1
            );
            const stringCompare = Cmp.string();
            controls.sort(
                Cmp.chain([
                    Cmp.map(
                        (control) =>
                            controlIdToControlAndActions[control.id]
                                .controlProgram.name,
                        stringCompare
                    ),
                    Cmp.map((control) => control.title, stringCompare),
                ])
            );
            const controlOptions: Array<
                TypeaheadOptionOrGroup<ControlOverview>
            > = [];
            let controlProgramData: {
                id: number;
                group: TypeaheadOptionGroup<ControlOverview>;
            } | null = null;
            // unassignedControls is already sorted by control program name
            controls.forEach((control) => {
                if (
                    !controlProgramData ||
                    control.controlProgramId !== controlProgramData.id
                ) {
                    controlProgramData = {
                        id: control.controlProgramId,
                        group: {
                            type: "Group",
                            value: (
                                <div className={theme.optionHeader}>
                                    {
                                        controlIdToControlAndActions[control.id]
                                            .controlProgram.name
                                    }
                                </div>
                            ),
                            options: [],
                        },
                    };
                    controlOptions.push(controlProgramData.group);
                }
                controlProgramData.group.options.push({
                    type: "Option",
                    value: control,
                });
            });
            callback(controlOptions);
            return Promise.resolve();
        };

        const InputWithTypeaheadField =
            InputWithTypeahead as unknown as React.ComponentClass<
                Omit<InputWithTypeaheadProps<ControlOverview>, "value">
            >;

        const riskCoverageForm = mapForm<{
            riskCoverage: number | undefined;
        }>()(
            form,
            simpleMappers(
                "riskCoverage",
                (coverage: number | null) => coverage ?? undefined,
                (coverage: number | undefined) => coverage ?? null
            )()
        );
        const controlId = form.getFieldValue("controlId");

        return (
            <Drawer
                onCancel={onCancel}
                buildHeader={() =>
                    t("RiskMapResponse_AssignControls_AddImperoControl")
                }
                buildFooter={() => (
                    <Button
                        variant="primary"
                        id="assign-control-save"
                        onClick={this.save}
                        disabled={
                            hasError(getFieldsError() as ObjectError) ||
                            controlId === undefined
                        }
                    >
                        {t("RiskMapResponse_AddControlButton")}
                    </Button>
                )}
            >
                <Form>
                    {getFieldDecorator("controlId", {
                        trigger: "", // Update handled by calls to setFieldsValue
                        getValueProps: (controlId) => ({
                            value:
                                controlId !== undefined
                                    ? controlIdToControlAndActions[controlId]
                                          .control.title
                                    : undefined,
                        }),
                        rules: [
                            {
                                required: true,
                                displayField: t(
                                    "RiskMapResponse_AssignControls_ViewControlPopup_Title"
                                ),
                            },
                        ],
                    })(
                        <InputWithTypeaheadField
                            id="assign-control-selector"
                            formatOption={(control) => control.title}
                            onSearch={onAutocomplete}
                            onSelect={(control) =>
                                form.setFieldsValue({ controlId: control.id })
                            }
                            placeholder={t(
                                "RiskMapResponse_AssignControls_ViewControlPopup_ControlTypeaheadPlaceholder"
                            )}
                            dropdownMatchSelectWidth
                        />
                    )}
                    {controlId !== undefined ? (
                        <>
                            <Divider />
                            {this.renderControl(controlId)}
                            <CreateSliderInput
                                id="riskCoverage"
                                label={t(
                                    "RiskDirectory_MitigatingControls_CoveragePercentage"
                                )}
                                form={riskCoverageForm}
                                initialValue={0}
                                min={0}
                                formatter={(value) => `${value}%`}
                                parser={(value) =>
                                    (value ?? "").replace("%", "")
                                }
                                validators={[
                                    {
                                        kind: "ruleset",
                                        type: "integer",
                                        min: 0,
                                    },
                                ]}
                            />
                        </>
                    ) : null}
                </Form>
            </Drawer>
        );
    };

    renderControl = (controlId: Guid) => {
        const { controlIdToControlAndActions } = this.props;
        const { control, controlProgram, controlAction, controlProgramAction } =
            controlIdToControlAndActions[controlId];
        return (
            <>
                <BaseFormDisplay
                    label={t(
                        "RiskMapResponse_AssignControls_ViewControlPopup_ControlProgramNameLabel"
                    )}
                >
                    {controlProgramAction !== null ? (
                        <a
                            href={buildControlProgramViewUrl(controlProgram.id)}
                            target="_blank"
                        >
                            {controlProgram.name}
                        </a>
                    ) : (
                        controlProgram.name
                    )}
                </BaseFormDisplay>
                <BaseFormDisplay
                    label={t(
                        "RiskMapResponse_AssignControls_ViewControlPopup_ControlTitleLabel"
                    )}
                >
                    {controlAction !== null ? (
                        <a
                            href={`${WebRoute.CONTROLS}/${control.id}`}
                            target="_blank"
                        >
                            {control.title}
                        </a>
                    ) : (
                        control.title
                    )}
                </BaseFormDisplay>
                <TextAreaInput
                    type="READ_ONLY"
                    label={t(
                        "RiskMapResponse_AssignControls_ViewControlPopup_DescriptionLabel"
                    )}
                    value={control.description ?? undefined}
                />
            </>
        );
    };

    save = () => {
        const { save, form } = this.props;

        form.validateFields({ force: true }, (errors: ObjectError, values) => {
            if (hasError(errors)) {
                return;
            }
            save(values);
        });
    };
}

interface AssignControlProps {
    controlIdToControlAndActions: Record<Guid, T.ControlAndActions>;
    unassignedControls: Array<ControlOverview>;
    onCancel: () => void;
    save: (values: AssignControlValues) => Promise<void>;
}

type AssignControlState = object;

type AssignControlValues = MitigatingControl;

const AssignControl = FormCreate<
    AssignControlValues,
    AssignControlProps & FormRootComponentProps<AssignControlValues>
>()(AssignControlForm);

export type { AssignControlProps, AssignControlState, AssignControlValues };
export { AssignControl };
